import React, { useState, useEffect } from 'react';
import {
    fetchCimaSuggestions,
    fetchCimaDetalle,
    fetchMedicationsByCategory,
    fetchAllMedications
} from '../../Service/Cima';

const MedicationSelection = ({ onMedicationSelect, onMedicationDetailsLoad }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [activeFilter, setActiveFilter] = useState('Todos');
    const [resultCount, setResultCount] = useState(0);
    const [loadingProgress, setLoadingProgress] = useState('');
    
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const ITEMS_PER_PAGE = 20;

    const filters = [
        'Todos', 'Financiados', 'Genéricos', 'Recientes', 
        'Favoritos', 'Analgésicos', 'Antibióticos', 'Antiinflamatorios'
    ];

    useEffect(() => {
        handleFilterChange('Todos', 1);
    }, []);

    useEffect(() => {
        if (activeFilter !== 'Todos' || !query.trim()) {
            return;
        }

        const searchTimeout = setTimeout(() => {
            setCurrentPage(1);
            setTotalPages(1);
            fetchCimaSuggestions({ nombre: query.trim() })
                .then(meds => {
                    if (meds && meds.length) {
                        setSuggestions(meds);
                        setResultCount(meds.length);
                    } else {
                        setSuggestions([]);
                        setResultCount(0);
                    }
                })
                .catch(err => {
                    console.error('Error búsqueda:', err);
                });
        }, 500);

        return () => clearTimeout(searchTimeout);
    }, [query, activeFilter]);

    const performSearch = async (searchQuery) => {
        if (!searchQuery.trim()) {
            handleFilterChange('Todos', 1);
            return;
        }
        
        setLoading(true);
        setCurrentPage(1);
        setTotalPages(1);
        
        try {
            const searchFilters = { nombre: searchQuery.trim() };
            const meds = await fetchCimaSuggestions(searchFilters);
            
            if (meds && Array.isArray(meds)) {
                setSuggestions(meds);
                setResultCount(meds.length);
            } else {
                setSuggestions([]);
                setResultCount(0);
            }
            setSelected(null);
            setDetails(null);
        } catch (err) {
            console.error('Error buscando medicamentos:', err);
            setSuggestions([]);
            setResultCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const newQuery = e.target.value;
        setQuery(newQuery);
        
        if (activeFilter !== 'Todos' && newQuery.trim()) {
            setActiveFilter('Todos');
        }
    };

    const handleSearch = async () => {
        await performSearch(query);
    };

    const handleFilterChange = async (filter, page = 1) => {
        setActiveFilter(filter);
        setCurrentPage(page);
        setLoading(true);
        setIsLoadingPage(page !== 1);
        setQuery('');
        setLoadingProgress(`Cargando ${filter} - Página ${page}...`);
        
        try {
            let meds = [];
            
            switch (filter) {
                case 'Financiados':
                    meds = await fetchCimaSuggestions({ comerc: '1' });
                    setTotalPages(1);
                    break;
                    
                case 'Genéricos':
                    const allMedsGeneric = await fetchAllMedications(page, {});
                    if (allMedsGeneric) {
                        meds = allMedsGeneric.filter(med => isGenerico(med));
                    }
                    setTotalPages(Math.min(50, Math.ceil(1000 / ITEMS_PER_PAGE))); 
                    break;
                    
                case 'Analgésicos':
                    meds = await fetchMedicationsByCategory('analgesicos');
                    setTotalPages(1);
                    break;
                    
                case 'Antibióticos':
                    meds = await fetchMedicationsByCategory('antibioticos');
                    setTotalPages(1);
                    break;
                    
                case 'Antiinflamatorios':
                    meds = await fetchMedicationsByCategory('antiinflamatorios');
                    setTotalPages(1);
                    break;
                    
                case 'Recientes':
                    meds = await fetchAllMedications(page, {});
                    setTotalPages(Math.min(10, Math.ceil(200 / ITEMS_PER_PAGE)));
                    break;
                    
                case 'Favoritos':
                    meds = await fetchAllMedications(page, {});
                    setTotalPages(Math.min(5, Math.ceil(100 / ITEMS_PER_PAGE)));
                    break;
                    
                default:
                    meds = await fetchAllMedications(page, {});
                    setTotalPages(Math.min(100, Math.ceil(2000 / ITEMS_PER_PAGE))); 
                    break;
            }
            
            setSuggestions(meds || []);
            setResultCount((meds || []).length);
            setSelected(null);
            setDetails(null);
        } catch (err) {
            console.error(`Error aplicando filtro ${filter}:`, err);
            setSuggestions([]);
            setResultCount(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
            setIsLoadingPage(false);
            setTimeout(() => setLoadingProgress(''), 1000);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && !isLoadingPage && !loading) {
            handleFilterChange(activeFilter, newPage);
        }
    };

    const PaginationControls = () => {
        if (totalPages <= 1) return null;
        
        const getVisiblePages = () => {
            const delta = 2;
            const range = [];
            const rangeWithDots = [];
            
            for (let i = Math.max(2, currentPage - delta); 
                 i <= Math.min(totalPages - 1, currentPage + delta); 
                 i++) {
                range.push(i);
            }
            
            if (currentPage - delta > 2) {
                rangeWithDots.push(1, '...');
            } else {
                rangeWithDots.push(1);
            }
            
            rangeWithDots.push(...range);
            
            if (currentPage + delta < totalPages - 1) {
                rangeWithDots.push('...', totalPages);
            } else if (totalPages > 1) {
                rangeWithDots.push(totalPages);
            }
            
            return [...new Set(rangeWithDots)];
        };
        
        return (
            <nav className="mt-4 pt-3 border-top">
                <ul className="pagination justify-content-center mb-0">
                    <li className={`page-item ${currentPage === 1 || isLoadingPage || loading ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || isLoadingPage || loading}
                        >
                            ← Anterior
                        </button>
                    </li>
                    
                    {getVisiblePages().map((page, index) => (
                        page === '...' ? (
                            <li key={`dots-${index}`} className="page-item disabled">
                                <span className="page-link">...</span>
                            </li>
                        ) : (
                            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''} ${isLoadingPage || loading ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                    disabled={isLoadingPage || loading}
                                >
                                    {page}
                                </button>
                            </li>
                        )
                    ))}
                    
                    <li className={`page-item ${currentPage === totalPages || isLoadingPage || loading ? 'disabled' : ''}`}>
                        <button 
                            className="page-link" 
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || isLoadingPage || loading}
                        >
                            Siguiente →
                        </button>
                    </li>
                </ul>
                <div className="text-center text-muted small mt-2">
                    Página {currentPage} de {totalPages}
                </div>
            </nav>
        );
    };

    const handleSelect = async (med) => {
        setSelected(med);
        setLoadingDetails(true);

        if (onMedicationSelect && typeof onMedicationSelect === 'function') {
            onMedicationSelect(med);
        }
        
        try {
            const registrationNumber = med.nregistro;
            if (registrationNumber) {
                const det = await fetchCimaDetalle(registrationNumber);
                setDetails(Array.isArray(det) ? det[0] : det);

                if (onMedicationDetailsLoad && typeof onMedicationDetailsLoad === 'function') {
                    onMedicationDetailsLoad(details);
                }
            }
        } catch (err) {
            console.error('Error obteniendo detalles:', err);
            setDetails(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    const getRegistrationNumber = (med) => {
        return med.nregistro || med.numeroRegistro || 'N/A';
    };

    const getMedicationName = (med) => {
        return med.nombre || 'Nombre no disponible';
    };

    const getLaboratory = (med) => {
        return med.labtitular || med.laboratorio || 'No especificado';
    };

    const isFinanciado = (med) => {
        return med.comerc === true;
    };

    const isGenerico = (med) => {
        return med.nombre && (
            med.nombre.includes('EFG') || 
            med.nombre.includes('efg') ||
            med.nombre.toLowerCase().includes('genérico')
        );
    };

    const needsPrescription = (med) => {
        return med.receta === true;
    };

    const hasTriangulo = (med) => {
        return med.triangulo === true;
    };

    const isHuerfano = (med) => {
        return med.huerfano === true;
    };

    const isBiosimilar = (med) => {
        return med.biosimilar === true;
    };

    const getPrice = () => {
        if (details && details.presentaciones && details.presentaciones.length > 0) {
            const firstPresentation = details.presentaciones[0];
            return firstPresentation.precio || 'No disponible';
        }
        return 'No disponible';
    };

    const getFormaFarmaceutica = () => {
        if (details && details.formaFarmaceutica) {
            return details.formaFarmaceutica;
        }
        if (selected && selected.nombre) {
            const nombre = selected.nombre.toLowerCase();
            if (nombre.includes('comprimido')) return 'Comprimido';
            if (nombre.includes('cápsula')) return 'Cápsula';
            if (nombre.includes('jarabe')) return 'Jarabe';
            if (nombre.includes('solución')) return 'Solución';
            if (nombre.includes('pomada')) return 'Pomada';
            if (nombre.includes('crema')) return 'Crema';
            if (nombre.includes('gel')) return 'Gel';
        }
        return 'No especificado';
    };

    const getViaAdministracion = () => {
        if (details && details.vias) {
            return details.vias;
        }
        const forma = getFormaFarmaceutica().toLowerCase();
        if (forma.includes('comprimido') || forma.includes('cápsula')) return 'Oral';
        if (forma.includes('pomada') || forma.includes('crema') || forma.includes('gel')) return 'Tópica';
        return 'No especificado';
    };

    return (
        <div className="card shadow-sm my-3">
            <div className="card-body p-4">
                <div className="d-flex align-items-center mb-4">
                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" 
                         style={{width: '32px', height: '32px', fontSize: '16px', fontWeight: 'bold'}}>
                        2
                    </div>
                    <h4 className="mb-0 text-primary fw-semibold">Selección del medicamento</h4>
                </div>

                <div className="row g-2 mb-4">
                    <div className="col">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar por nombre de medicamento, principio activo o código nacional..."
                            value={query}
                            onChange={handleInputChange}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            disabled={loading || isLoadingPage}
                        />
                    </div>
                    <div className="col-auto">
                        <button 
                            className="btn btn-primary"
                            onClick={handleSearch}
                            disabled={loading || isLoadingPage}
                        >
                            {loading ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-4">
                    {filters.map((filter) => (
                        <button
                            key={filter}
                            className={`btn btn-sm ${activeFilter === filter ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => handleFilterChange(filter, 1)}
                            disabled={loading || isLoadingPage}
                            style={{borderRadius: '20px'}}
                        >
                            {filter}
                            {activeFilter === filter && resultCount > 0 && !loading && !isLoadingPage && (
                                <span> ({resultCount})</span>
                            )}
                        </button>
                    ))}
                </div>

                {(loading || isLoadingPage) && (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Cargando...</span>
                        </div>
                        {loadingProgress || (activeFilter === 'Todos' && query.trim() ? 'Buscando medicamentos...' : 'Cargando medicamentos...')}
                        {isLoadingPage && <span> 🔄</span>}
                    </div>
                )}

                {!loading && !isLoadingPage && resultCount > 0 && (
                    <div className="alert alert-info py-2 mb-3">
                        {query.trim() ? 
                            `Se encontraron ${resultCount} medicamentos para "${query}"` :
                            `Página ${currentPage}: Mostrando ${resultCount} medicamentos en "${activeFilter}"`
                        }
                        {totalPages > 1 && !query.trim() && (
                            <small className="text-muted ms-2">
                                (Estimado: ~{totalPages * ITEMS_PER_PAGE} medicamentos totales)
                            </small>
                        )}
                    </div>
                )}

                <div className="border rounded p-3" style={{maxHeight: '500px', overflowY: 'auto'}}>
                    {suggestions.length === 0 && !loading && !isLoadingPage && (
                        <div className="text-center py-5 text-muted bg-light rounded">
                            {query.trim() ? 
                                `No se encontraron medicamentos para "${query}"` :
                                `No se encontraron medicamentos en la categoría "${activeFilter}"`
                            }
                        </div>
                    )}
                    
                    {suggestions.map((med, index) => {
                        const registrationNumber = getRegistrationNumber(med);
                        const isSelected = selected && getRegistrationNumber(selected) === registrationNumber;
                        
                        return (
                            <div
                                key={registrationNumber + index}
                                className={`card mb-2 ${isSelected ? 'border-primary bg-light' : ''}`}
                            >
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-start">
                                        <div className="me-3" style={{fontSize: '24px'}}>💊</div>
                                        <div className="flex-grow-1">
                                            <h6 className="mb-2 fw-semibold">{getMedicationName(med)}</h6>
                                            
                                            <div className="d-flex flex-wrap gap-1 mb-2">
                                                {isFinanciado(med) && (
                                                    <span className="badge bg-success">Financiado</span>
                                                )}
                                                {isGenerico(med) && (
                                                    <span className="badge bg-warning text-dark">Genérico</span>
                                                )}
                                                {needsPrescription(med) && (
                                                    <span className="badge bg-info">Requiere receta</span>
                                                )}
                                                {hasTriangulo(med) && (
                                                    <span className="badge bg-warning text-dark">▲ Triángulo</span>
                                                )}
                                                {isHuerfano(med) && (
                                                    <span className="badge bg-danger">Huérfano</span>
                                                )}
                                                {isBiosimilar(med) && (
                                                    <span className="badge bg-success">Biosimilar</span>
                                                )}
                                            </div>
                                            
                                            <small className="text-muted">
                                                CN: {registrationNumber}
                                                {getLaboratory(med) !== 'No especificado' && ` • Laboratorio: ${getLaboratory(med)}`}
                                                {med.pactivos && ` • ${med.pactivos}`}
                                            </small>
                                        </div>
                                        <button 
                                            className={`btn btn-sm ms-3 ${isSelected ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => handleSelect(med)}
                                        >
                                            {isSelected ? 'Seleccionado' : 'Seleccionar'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <PaginationControls />

                {selected && (
                    <>
                        <h5 className="mt-4 pt-4 border-top fw-semibold">Medicamento seleccionado</h5>

                        <div className="card bg-light border-primary mt-3">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center mb-3 pb-3 border-bottom">
                                    <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" 
                                         style={{width: '40px', height: '40px', fontSize: '24px'}}>
                                        💊
                                    </div>
                                    <h5 className="mb-0">{getMedicationName(selected)}</h5>
                                </div>
                                
                                {loadingDetails ? (
                                    <div className="text-center py-4 text-muted fst-italic">
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Cargando información detallada...
                                    </div>
                                ) : (
                                    <>
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Código Nacional</small>
                                                <div className="fw-semibold">{getRegistrationNumber(selected)}</div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Laboratorio</small>
                                                <div className="fw-semibold">{getLaboratory(selected)}</div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Principio activo</small>
                                                <div className="fw-semibold">
                                                    {selected.pactivos || 
                                                     (details && details.pactivos) || 
                                                     'No especificado'}
                                                </div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Forma farmacéutica</small>
                                                <div className="fw-semibold">{getFormaFarmaceutica()}</div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Vía de administración</small>
                                                <div className="fw-semibold">{getViaAdministracion()}</div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Financiado</small>
                                                <div className="fw-semibold">{isFinanciado(selected) ? 'Sí' : 'No'}</div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Requiere receta</small>
                                                <div className="fw-semibold">{needsPrescription(selected) ? 'Sí' : 'No'}</div>
                                            </div>
                                            
                                            <div className="col-md-6">
                                                <small className="text-muted text-uppercase d-block mb-1">Precio</small>
                                                <div className="fw-semibold text-primary">
                                                    {getPrice() !== 'No disponible' ? `${getPrice()}€` : 'No disponible'}
                                                </div>
                                            </div>
                                        </div>

                                        {details && details.presentaciones && details.presentaciones.length > 1 && (
                                            <div className="mt-3 pt-3 border-top">
                                                <h6 className="text-uppercase small fw-semibold mb-3">Otras presentaciones disponibles</h6>
                                                <div className="d-flex flex-column gap-2">
                                                    {details.presentaciones.slice(1, 4).map((pres, idx) => (
                                                        <div key={idx} className="card card-body py-2 px-3">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <small>CN: {pres.cn}</small>
                                                                {pres.precio && <span className="badge bg-primary">{pres.precio}€</span>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {(isGenerico(selected) || hasTriangulo(selected) || isHuerfano(selected) || isBiosimilar(selected)) && (
                                            <div className="mt-3 pt-3 border-top">
                                                <h6 className="text-uppercase small fw-semibold mb-3">Características especiales</h6>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {isGenerico(selected) && (
                                                        <span className="badge bg-success">Medicamento genérico (EFG)</span>
                                                    )}
                                                    {hasTriangulo(selected) && (
                                                        <span className="badge bg-warning text-dark">Triángulo negro - Seguimiento adicional</span>
                                                    )}
                                                    {isHuerfano(selected) && (
                                                        <span className="badge bg-danger">Medicamento huérfano</span>
                                                    )}
                                                    {isBiosimilar(selected) && (
                                                        <span className="badge bg-success">Medicamento biosimilar</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MedicationSelection;